import { useTranslation } from "react-i18next";
import { ErrorBox, Skeleton } from "../../components/ui";
import { useConfigureGratuity, useGratuity, useRaiseGratuity } from "../../lib/api";
import { Money, SectionCard, Stat } from "../labour/shared";

/** End-of-service indemnity, and what it totals to (HRX-07).
 *
 * `Gratuity Rule` and `Gratuity` both held zero rows, which is why wave P's full-and-final screen had to name
 * gratuity as excluded. Nobody on this site could see what the customer would owe its workforce if it closed tomorrow —
 * a statutory obligation that accrues silently every month and appears in full only when somebody resigns is
 * the definition of an unprovided liability.
 *
 * The fractions are **indicative**. They carry the shape of the DRC end-of-service indemnity so the mechanism
 * can be exercised and audited; the numbers belong to WP-16, and the convention matters as much as the
 * fractions — applying one band to every year rather than each band to the years inside it pays an
 * eleven-year employee eleven months instead of 6.75. */
export default function Gratuity({ company }: { company: string }) {
  const { t, i18n } = useTranslation();
  const lng = i18n.language;
  const q = useGratuity(company || undefined);
  const configure = useConfigureGratuity();
  const raise = useRaiseGratuity();

  if (q.isError) return <div className="mt-6"><ErrorBox error={q.error} onRetry={() => void q.refetch()} /></div>;
  if (!q.data) return <div className="mt-6 grid gap-3 md:grid-cols-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-24" />)}</div>;
  const d = q.data;
  const configured = Boolean(d.rule.configured);
  const slabs = (d.rule.slabs ?? []) as { from_year: number; to_year: number; fraction_per_year: number }[];

  return (
    <div className="mt-4 space-y-4" data-payroll-gratuity>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4" data-payroll-gratuity-strip>
        <Stat label={t("pay.liability")} value={<Money value={d.summary.amount} lng={lng} />}
          cls={d.summary.amount ? "text-status-amber" : ""} hint={t("pay.liabilityHint")} />
        <Stat label={t("pay.qualifying")} value={d.summary.qualifying}
          hint={t("pay.qualifyingHint", { n: d.summary.not_yet_qualifying })} />
        <Stat label={t("pay.monthsOwed")} value={d.summary.months.toFixed(1)} hint={t("pay.monthsOwedHint")} />
        <Stat label={t("pay.averageService")} value={`${d.summary.average_years} ${t("pay.years")}`}
          hint={t("pay.averageServiceHint")} />
      </div>

      <SectionCard title={t("pay.rule")} aside={configured ? String(d.rule.name ?? "") : t("pay.notConfigured")}>
        <div className="p-4">
          <p className="max-w-4xl text-sm text-brand-grey">{t("pay.ruleHint")}</p>
          <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px]" data-payroll-slabs>
            {slabs.map((s, i) => (
              <span key={i} className="rounded border border-brand-grey-light px-2 py-1">
                {t("pay.slab", { from: s.from_year, to: s.to_year || "…", fraction: s.fraction_per_year })}
              </span>
            ))}
            <span className="rounded border border-brand-grey-light px-2 py-1">
              {t("pay.method", { method: String(d.rule.calculate_gratuity_amount_based_on ?? "") })}
            </span>
          </div>
          {!configured && d.can_configure && (
            <button onClick={() => configure.mutate()} disabled={configure.isPending}
              data-payroll-configure-gratuity
              className="mt-3 rounded bg-brand-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
              {t("pay.createRule")}
            </button>
          )}
        </div>
      </SectionCard>

      {configure.isError && <ErrorBox error={configure.error} />}
      {raise.isError && <ErrorBox error={raise.error} />}

      <SectionCard title={t("pay.perPerson")} aside={t("pay.peopleN", { n: d.rows.length })}>
        <div className="max-h-[36rem] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-[#F6F8FA] text-left text-[11px] uppercase tracking-wide text-brand-grey">
              <tr>
                <th className="px-4 py-2">{t("wfa.person")}</th>
                <th className="px-2 py-2 text-right">{t("pay.service")}</th>
                <th className="px-2 py-2 text-right">{t("pay.monthlyPay")}</th>
                <th className="px-2 py-2 text-right">{t("pay.months")}</th>
                <th className="px-2 py-2 text-right">{t("pay.indemnity")}</th>
                <th className="px-4 py-2">{t("ops.status")}</th>
              </tr>
            </thead>
            <tbody>
              {d.rows.map((r) => (
                <tr key={r.name} className="border-t border-brand-grey-light" data-payroll-gratuity-row={r.name}>
                  <td className="px-4 py-2">
                    <div className="font-medium">
                      {r.employee_name}
                      {r.status !== "Active" && <span className="ml-2 rounded bg-brand-grey-light px-2 py-0.5 text-[10px] font-semibold text-brand-grey">{r.status}</span>}
                    </div>
                    <div className="text-[11px] text-brand-grey">{r.designation} · {r.company}</div>
                  </td>
                  <td className="px-2 py-2 text-right tabular-nums">{r.years}</td>
                  <td className="px-2 py-2 text-right tabular-nums text-brand-grey"><Money value={r.monthly_pay} lng={lng} /></td>
                  <td className="px-2 py-2 text-right tabular-nums">{r.qualifies ? r.months : "–"}</td>
                  <td className="px-2 py-2 text-right font-semibold"><Money value={r.amount} lng={lng} /></td>
                  <td className="px-4 py-2">
                    {!r.qualifies ? (
                      <span className="text-[11px] text-brand-grey">{t("pay.belowQualifying")}</span>
                    ) : r.raised ? (
                      <span className="rounded bg-green-50 px-2 py-0.5 text-[11px] font-semibold text-status-green"
                        data-payroll-gratuity-raised={r.name}>
                        {r.raised.name} · {r.raised.status}
                      </span>
                    ) : r.status !== "Active" && d.can_configure && configured ? (
                      <button onClick={() => raise.mutate({ employee: r.name })} disabled={raise.isPending}
                        data-payroll-raise={r.name}
                        className="rounded bg-brand-primary px-3 py-1 text-xs font-semibold text-white disabled:opacity-50">
                        {t("pay.raise")}
                      </button>
                    ) : (
                      <span className="text-[11px] text-brand-grey">{t("pay.accruing")}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>
    </div>
  );
}
